import React, { useState } from "react";

interface ToolTipProps {
  text: string | React.ReactNode;
  children: React.ReactNode;
  width?: string;
  maxWidth?: string;
  minWidth?: string;
  xOffset?: number;
  yOffset?: number;
  topLevelStyles?: React.CSSProperties;
  inline?: boolean;
}

const ToolTip = ({
  text,
  children,
  width,
  maxWidth,
  minWidth,
  xOffset = 10,
  yOffset = 60,
  topLevelStyles = {},
  inline = false,
}: ToolTipProps) => {
  const [isHovering, setIsHovering] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  if (inline) {
    topLevelStyles.display = "inline-block";
  }

  const handleMouseEnter = () => {
    setIsHovering(true); // Enable the tooltip when the mouse enters the child component.
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    // Calculate tooltip position based on the mouse coordinates.
    const { clientX, clientY } = event;

    // Set tooltip position dynamically according to the mouse position.
    setTooltipPosition({ x: clientX, y: clientY });
  };

  const handleMouseLeave = () => {
    setIsHovering(false); // Disable the tooltip when the mouse leaves the child component.
  };

  // If no tooltip text, then don't render the tooltip
  if (!text || (typeof text === "string" && text.trim() === "")) {
    return <>{children}</>;
  }

  return (
    <div
      className="relative"
      style={topLevelStyles}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div onMouseMove={handleMouseMove}>{children}</div>
      {isHovering && (
        <div
          style={{
            left: tooltipPosition.x + xOffset + "px",
            top: tooltipPosition.y + yOffset + "px",
            position: "fixed", // Use 'fixed' to position according to the viewport.
            pointerEvents: "none", // Prevent the tooltip from blocking mouse events.
            width: width,
            minWidth: minWidth,
            maxWidth: maxWidth,
            color: "white",
            backgroundColor: "#605C6F",
            fontFamily: "Roboto",
            fontWeight: "normal",
            textAlign: "left",
            padding: "0.5rem 1rem",
            zIndex: 10,
            transform: "translate(-50%, -100%)",
            borderRadius: "0.25rem",
            letterSpacing: "unset",
            textTransform: "none",
            wordWrap: "break-word",
            overflowWrap: "break-word",
            wordBreak: "break-word",
            whiteSpace: "normal", // Allow text to wrap for max-width configuration
          }}
          className="absolute px-4 py-2 text-sm leading-4 tracking-wider transform -translate-x-1/2 -translate-y-full opacity-100 rounded"
        >
          {text}
        </div>
      )}
    </div>
  );
};

export default ToolTip;
